/**
 * Text-offset helpers
 *
 * Translate between two coordinate systems for text inside an arbitrary
 * host element:
 *
 *   1. **Block-text offsets** — flat character indices into the host's
 *      `textContent`. This is what `text_range` selectors persist
 *      (`{ start, end }`).
 *   2. **DOM positions** — `(node, offsetWithinNode)` pairs that the
 *      browser's `Range` and `Selection` APIs operate on.
 *
 * Used by:
 *   - the selection-toolbar pipeline (DOM → offsets), which persists a
 *     comment's `text_range` anchor for later
 *   - the annotation overlay (offsets → DOM → `getClientRects()`),
 *     which paints persisted highlights inline
 *
 * The same helpers run in two modes:
 *   - **expanded** — host is `.block-detail-modal__panel`
 *   - **regular** — host is the stream card's `.block-view`
 *
 * The host is supplied by the caller so the conversion logic doesn't
 * have to know about either mode. Round-tripping is symmetric — what
 * we save is exactly what we resolve back, modulo content edits.
 */
// ---------------------------------------------------------------------------
// DOM → offset
// ---------------------------------------------------------------------------
/**
 * Walk `root`'s text descendants and return the flat-text offset of
 * `(targetNode, targetOffset)`. Returns `-1` if the target isn't a
 * descendant of `root` — callers should treat that as "couldn't
 * resolve" and skip persistence rather than write a broken offset.
 *
 * Approach matches the standard "DOM range → linear text offset"
 * recipe: TreeWalker over all text-node descendants, summing their
 * lengths until we reach the target. Element-node endpoints
 * (e.g. between paragraphs) are resolved by walking into the indexed
 * child first.
 */
export function computePlainTextOffset(root, targetNode, targetOffset) {
    let resolvedNode = targetNode;
    let resolvedOffset = targetOffset;
    // Element endpoint: `targetOffset` indexes into childNodes. Resolve
    // by summing the text in every preceding child + the prefix from
    // root up to this element, treating "after the last child" as the
    // total subtree length.
    if (resolvedNode.nodeType === Node.ELEMENT_NODE) {
        const children = resolvedNode.childNodes;
        if (resolvedOffset < children.length) {
            const child = children[resolvedOffset];
            let acc = 0;
            const walker = document.createTreeWalker(resolvedNode, NodeFilter.SHOW_TEXT);
            let current = walker.nextNode();
            while (current) {
                if (child.compareDocumentPosition(current) &
                    Node.DOCUMENT_POSITION_PRECEDING) {
                    acc += current.data.length;
                }
                current = walker.nextNode();
            }
            const prefix = computePlainTextOffset(root, resolvedNode, 0);
            if (prefix < 0)
                return -1;
            return prefix + acc;
        }
        // `targetOffset === childNodes.length` → "after the last child".
        let acc = 0;
        const walker = document.createTreeWalker(resolvedNode, NodeFilter.SHOW_TEXT);
        let current = walker.nextNode();
        while (current) {
            acc += current.data.length;
            current = walker.nextNode();
        }
        const prefix = computePlainTextOffset(root, resolvedNode, 0);
        if (prefix < 0)
            return -1;
        return prefix + acc;
    }
    // Leaf-text case: walk every text node under `root` until we hit
    // the target, summing characters as we go.
    let total = 0;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let current = walker.nextNode();
    while (current) {
        if (current === resolvedNode) {
            return total + resolvedOffset;
        }
        total += current.data.length;
        current = walker.nextNode();
    }
    return -1;
}
/**
 * Inverse of `computePlainTextOffset`: given a flat-text offset into
 * `root`, return the `(text node, offset within node)` it lands on.
 * Returns `undefined` when the offset is past the end of `root`'s
 * flattened text.
 *
 * `offset === total` (one past the last character) clamps to the very
 * end of the last text node so building a Range that ends "after
 * everything" still works.
 */
function resolveOffsetToDomPosition(root, offset) {
    if (offset < 0)
        return undefined;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let cumulative = 0;
    let lastTextNode = undefined;
    let current = walker.nextNode();
    while (current) {
        const textNode = current;
        const len = textNode.data.length;
        // Lands inside this text node when `offset` is strictly less than
        // the cumulative length after this node — i.e. there's still
        // characters left in this run that the offset points at.
        if (offset <= cumulative + len) {
            return { node: textNode, offsetInNode: offset - cumulative };
        }
        cumulative += len;
        lastTextNode = textNode;
        current = walker.nextNode();
    }
    // `offset === total length` — clamp to the end of the last text
    // node (e.g. a `text_range` whose `end` is the last char of the
    // host's text).
    if (lastTextNode && offset === cumulative) {
        return { node: lastTextNode, offsetInNode: lastTextNode.data.length };
    }
    return undefined;
}
// ---------------------------------------------------------------------------
// offset range → Range (and back to rects)
// ---------------------------------------------------------------------------
/**
 * Build a live DOM `Range` over `root` from a `[start, end)` flat-text
 * offset pair. Returns `undefined` when either endpoint can't be
 * resolved (e.g. the host content shrank since the offsets were
 * persisted, or the host isn't mounted).
 *
 * Callers can drop the returned Range into `getClientRects()` to
 * paint per-line overlay rects, or into `scrollIntoView()` (via a
 * temporary `Selection`) to bring the range into view.
 */
export function buildRangeFromOffsets(root, start, end) {
    if (end < start)
        return undefined;
    const startPos = resolveOffsetToDomPosition(root, start);
    const endPos = resolveOffsetToDomPosition(root, end);
    if (!startPos || !endPos)
        return undefined;
    const range = document.createRange();
    try {
        range.setStart(startPos.node, startPos.offsetInNode);
        range.setEnd(endPos.node, endPos.offsetInNode);
    }
    catch {
        return undefined;
    }
    return range;
}
// Walk the text nodes touched by `range` and emit per-node
// `{ node, from, to }` segments clamped to the range's endpoints.
// The total length of the emitted segments equals `range.toString().length`
// (the spec defines `Range.toString()` as the concatenation of these same
// clamped text fragments, with no synthetic separators), so character
// counts measured against the string round-trip back into DOM positions
// via the segment list.
function collectRangeTextSegments(range) {
    const segments = [];
    const { startContainer, startOffset, endContainer, endOffset } = range;
    // Single-text-node selection — the typical case for a one-line drag.
    // Skip the TreeWalker setup entirely.
    if (startContainer === endContainer &&
        startContainer.nodeType === Node.TEXT_NODE) {
        if (startOffset < endOffset) {
            segments.push({
                node: startContainer,
                from: startOffset,
                to: endOffset,
            });
        }
        return segments;
    }
    const walker = document.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_TEXT);
    let current = walker.nextNode();
    while (current) {
        const text = current;
        if (range.intersectsNode(text)) {
            let from = 0;
            let to = text.data.length;
            if (text === startContainer)
                from = startOffset;
            if (text === endContainer)
                to = endOffset;
            if (from < to)
                segments.push({ node: text, from, to });
        }
        current = walker.nextNode();
    }
    return segments;
}
// Walk forward through `segments`, consuming `count` characters; return the
// DOM position the count lands on. Falls through (returns `undefined`) only
// when `count` exceeds the segments' total length, which the caller guards
// against.
function advanceFromStart(segments, count) {
    let remaining = count;
    for (const seg of segments) {
        const segLen = seg.to - seg.from;
        if (remaining <= segLen) {
            return { node: seg.node, offset: seg.from + remaining };
        }
        remaining -= segLen;
    }
    return undefined;
}
// Symmetric to `advanceFromStart` but walking backward from the last segment.
// Used to find where the trailing-whitespace tail begins so we can clip
// the range's end inward.
function retreatFromEnd(segments, count) {
    let remaining = count;
    for (let i = segments.length - 1; i >= 0; i--) {
        const seg = segments[i];
        const segLen = seg.to - seg.from;
        if (remaining <= segLen) {
            return { node: seg.node, offset: seg.to - remaining };
        }
        remaining -= segLen;
    }
    return undefined;
}
/**
 * Return a NEW `Range` with leading/trailing whitespace excluded, leaving
 * internal whitespace untouched. Used to clip the paint surface of a
 * text selection before measuring `getClientRects()` — browsers emit
 * small line-end fragments for trailing whitespace (and for the line-
 * break boundary when a selection wraps), which read as phantom yellow
 * tiles in the modal's live selection overlay and persisted text
 * highlights even though the user's intent was just "this run of
 * visible text."
 *
 * Persisted `text_range` offsets (saved by `useBlockDetailSelection`'s
 * `resolveRangeToTextRange`) are computed from the *original* live
 * range, so trimming here only affects what gets painted — never what
 * gets stored.
 *
 * Fail-soft: returns the input range unchanged when the range has no
 * leading/trailing whitespace, is entirely whitespace, or any step in
 * the conversion throws. We never want a paint-time helper to crash a
 * selection overlay; the existing zero-size-rect filter still drops
 * the residual fragments engines emit at line-break boundaries when
 * trimming alone isn't enough.
 */
export function cloneTrimmedRange(range) {
    try {
        const text = range.toString();
        const trimmedStart = text.trimStart();
        const trimmedBoth = trimmedStart.trimEnd();
        const leading = text.length - trimmedStart.length;
        const trailing = trimmedStart.length - trimmedBoth.length;
        if (leading === 0 && trailing === 0)
            return range;
        // Entirely-whitespace range — nothing visible to paint anyway, so
        // let the zero-size filter in `getRangeClientRectsRelativeTo` do
        // its existing job rather than collapsing the range to a point.
        if (trimmedBoth.length === 0)
            return range;
        const segments = collectRangeTextSegments(range);
        if (segments.length === 0)
            return range;
        const newStart = advanceFromStart(segments, leading);
        const newEnd = retreatFromEnd(segments, trailing);
        if (!newStart || !newEnd)
            return range;
        const trimmed = document.createRange();
        trimmed.setStart(newStart.node, newStart.offset);
        trimmed.setEnd(newEnd.node, newEnd.offset);
        return trimmed;
    }
    catch {
        return range;
    }
}
// True when `node`'s nearest element has computed `user-select: none`,
// so its text glyphs are part of the DOM but the user wasn't meant to
// select them (date chip, attribution avatars, replier-stack initials,
// drawing wrappers, etc.). The browser's Range API ignores `user-
// select` entirely — a drag that spans across these elements happily
// pulls their text content into the Range — but `::selection` paints
// respect it, so the native highlight visually skips them. Live
// selection overlays opt into this check via `filterUnselectable` so
// their painted rects line up with what native selection shows.
function isTextNodeUnselectable(node) {
    const el = node.parentElement;
    if (!el)
        return false;
    try {
        const style = window.getComputedStyle(el);
        // `user-select` is widely supported; the `-webkit-` prefix matters
        // on iOS Safari, where the unprefixed property reports its default
        // even when the prefixed one is honored.
        return (style.userSelect === "none" ||
            style.webkitUserSelect ===
                "none");
    }
    catch {
        return false;
    }
}
/**
 * Convert a Range's `getClientRects()` into plain rects in
 * host-local coordinates. Three filters apply:
 *
 *   1. Leading/trailing whitespace is trimmed off the range (via
 *      `cloneTrimmedRange`) before measurement, so a selection like
 *      "...the long form " doesn't paint a phantom yellow tile in
 *      the line-end gutter for the trailing space. Always on.
 *   2. (Opt-in via `options.filterUnselectable`) Text nodes whose
 *      nearest element has computed `user-select: none` are
 *      skipped — see the option's doc for when to pass it.
 *   3. Zero-size rects are dropped — browsers still emit those at
 *      line-break boundaries.
 *
 * `origin` is typically the host's `getBoundingClientRect()` (call
 * `getHostRect(host)` for that), but callers can pass any origin
 * point if they want a different reference frame (e.g. an inner
 * scroll container).
 */
export function getRangeClientRectsRelativeTo(range, origin, options) {
    const measured = cloneTrimmedRange(range);
    // Persisted-highlight path: a single call into `Range.getClient
    // Rects()` returns line-level rects (one per visual wrap), which
    // produces fewer, cleaner overlay tiles than the per-segment path
    // below. The user-select filter is the only reason to walk
    // segments individually, so we skip the extra work when it's off.
    if (!options?.filterUnselectable) {
        return collectRectsFromList(measured.getClientRects(), origin);
    }
    // Live-selection path: walk per-text-node segments and skip any
    // whose parent element has computed `user-select: none`, then
    // build a sub-range over each selectable segment and concatenate
    // its rects. Per-segment is the only way to surgically exclude
    // chrome — `Range.getClientRects()` on the whole range has no way
    // to ignore portions of it.
    const segments = collectRangeTextSegments(measured);
    const rects = [];
    for (const seg of segments) {
        if (isTextNodeUnselectable(seg.node))
            continue;
        let sub;
        try {
            sub = document.createRange();
            sub.setStart(seg.node, seg.from);
            sub.setEnd(seg.node, seg.to);
        }
        catch {
            continue;
        }
        pushRectsFromList(sub.getClientRects(), origin, rects);
    }
    return rects;
}
function collectRectsFromList(list, origin) {
    const rects = [];
    pushRectsFromList(list, origin, rects);
    return rects;
}
function pushRectsFromList(list, origin, out) {
    for (let i = 0; i < list.length; i++) {
        const r = list[i];
        if (r.width === 0 || r.height === 0)
            continue;
        out.push({
            top: r.top - origin.top,
            left: r.left - origin.left,
            width: r.width,
            height: r.height,
        });
    }
}
/**
 * Look up the live host rect so callers can shift viewport-space range
 * rects into host-local coordinates. Returns `undefined` when the
 * host isn't mounted.
 *
 * Replaces the previous panel-only `getPanelRect()` helper. Pass any
 * Element here — the modal panel for the expanded mode, the card's
 * `.block-view` for the regular (stream) mode.
 */
export function getHostRect(host) {
    if (!host)
        return undefined;
    return host.getBoundingClientRect();
}
/**
 * Find the **text host** — the element whose flattened text owns the
 * `text_range` offsets — given any element inside the
 * `AnnotationViewContainer` tree. The text host is *not* the container
 * itself (which is a transparent overlay layer); it's the sibling
 * content node that
 * actually contains the rendered block text.
 *
 * Resolution rules:
 *   - mode = "expanded": the modal panel (`.block-detail-modal__panel`)
 *     — its descendant tree includes the BlockView with the text.
 *   - mode = "regular":  the stream card's `.block-view` element —
 *     a sibling of the `AnnotationViewContainer` inside the same
 *     `.block-view-host`. We walk up to the host then back down to
 *     the card so a swap of the container's position (e.g. above vs
 *     below the card) doesn't break the lookup.
 *
 * Returns `undefined` until the surrounding chrome is mounted; callers
 * should bail out and try again next frame in that case (rAF +
 * scroll/resize re-measures handle this naturally).
 */
export function findTextHost(fromElement, mode) {
    if (!fromElement)
        return undefined;
    if (mode === "expanded") {
        const panel = fromElement.closest(".block-detail-modal__panel");
        return panel ?? undefined;
    }
    const blockViewHost = fromElement.closest(".block-view-host");
    if (!blockViewHost)
        return undefined;
    const blockView = blockViewHost.querySelector(":scope > .block-view");
    return blockView ?? undefined;
}
